import React, { useState, useEffect, useContext } from 'react';
import {
  Avatar,
  AvatarGroup,
  Clickable,
  Drawer,
  Tabs,
} from '@fashionnovateam/nova-ui-toolkit';
import { useForm, FormProvider } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import Header from './Header';
import { useCounterContext } from '../hooks/useCounter';
import QuoteTab from './QuoteTab';
import { useEditQuote } from '../hooks/queries';
import toast from '../../Toast/Toast';
import Chat from './Chat';
import QUERY_KEYS from '../../../Lib/Utils/Const/queries-keys';
import { FeatureFlagsContext } from '../../../Lib/Contexts/FeatureFlagsContext';
import { CounterQuoteFormValues, QuantityItem } from '../types';
import {
  prepareCounterFormDataForAPI,
  transformAPIResponseToCounterForm,
  cleanGarmentsForAPI,
} from '../utils/fabricMigration';

type ContactDrawerProps = {
  isOpen: { opened: boolean; tab: 'quote' | 'chat' };
  onClose: () => void;
};

const isAnyRange = (range: QuantityItem) => {
  if (!range || range.min_range === '') return 'any';
  // Cast min_range and max_range to numbers before comparison
  const minRange = typeof range.min_range === 'number' ? range.min_range : -1;
  const maxRange = typeof range.max_range === 'number' ? range.max_range : -1;
  return minRange > -1 && maxRange > -1 ? 'range' : 'any';
};

export function CounterDrawer({ isOpen, onClose }: ContactDrawerProps) {
  const [selectedTab, setSelectedTab] = useState<'quote' | 'chat'>(isOpen.tab);
  const { selectedCounter, userId } = useCounterContext();
  const editQuoteMutation = useEditQuote();
  const queryClient = useQueryClient();
  const { flags } = useContext(FeatureFlagsContext);
  const closeDrawer = () => {
    onClose();
  };

  useEffect(() => {
    setSelectedTab(isOpen.tab);
  }, [isOpen.tab]);

  const baselineJrCost: number | '' = selectedCounter?.baseline_jr_cost ?? '';
  const baselinePlusCost: number | '' =
    selectedCounter?.baseline_plus_cost ?? '';

  const counterRanges: QuantityItem[] =
    selectedCounter?.counter_quote?.ranges?.map((range: QuantityItem) => ({
      min_range: range.min_range ?? '',
      max_range: range.max_range ?? '',
      jr_cost: range.jr_cost ?? '',
      plus_cost: range.plus_cost ?? '',
      ...(flags?.baseline_cost?.enabled
        ? {
            baseline_jr_cost: range.baseline_jr_cost ?? baselineJrCost,
            baseline_plus_cost: range.baseline_plus_cost ?? baselinePlusCost,
          }
        : {}),
    })) || [
      {
        min_range: '',
        max_range: '',
        jr_cost: '',
        plus_cost: '',
        ...(flags?.baseline_cost?.enabled
          ? {
              baseline_jr_cost: baselineJrCost,
              baseline_plus_cost: baselinePlusCost,
            }
          : {}),
      },
    ];

  const getFormValues = () => {
    const transformedQuoteData = selectedCounter?.counter_quote
      ? transformAPIResponseToCounterForm(selectedCounter.counter_quote)
      : {};

    const ranges = selectedCounter?.counter_quote?.ranges;
    const isPlusCostNotApplicable =
      Array.isArray(ranges) &&
      ranges.length > 0 &&
      ranges.every((range) => range.plus_cost === null);

    return {
      quantity:
        selectedCounter?.counter_quote != null &&
        selectedCounter?.counter_quote?.ranges?.[0]?.min_range !== -1
          ? counterRanges
          : counterRanges[0],
      moq: selectedCounter?.counter_quote?.moq || '',
      mcq: selectedCounter?.counter_quote?.mcq || '',
      lead_time: selectedCounter?.counter_quote?.lead_time_in_weeks || '',
      reorder_lead_time:
        selectedCounter?.counter_quote?.ro_lead_time_in_weeks || '',
      coo: selectedCounter?.counter_quote?.coo || '',
      shipment_mode: selectedCounter?.counter_quote?.shipment_mode || '',
      fabric_content: selectedCounter?.counter_quote?.fabric_content || '',
      fabric_code_name: selectedCounter?.counter_quote?.fabric_code_name || '',
      weight: Number(selectedCounter?.counter_quote?.fabric_weight) || 0,
      fabric_type: selectedCounter?.counter_quote?.fabric_type || '',
      fabric_content_list: transformedQuoteData.fabric_content_list || [
        { id: 'fiber-0', name: '', percentage: 0 },
      ],
      contrast: transformedQuoteData.contrast,
      lining: transformedQuoteData.lining,
      filling: transformedQuoteData.filling,
      backing: transformedQuoteData.backing,
      coating: transformedQuoteData.coating,
      secondaryFabrics: transformedQuoteData.secondaryFabrics || [],
      hasPolyurethaneCoating: transformedQuoteData.hasPolyurethaneCoating,
      isMultiGarment: transformedQuoteData.isMultiGarment,
      garments: transformedQuoteData.garments,
      isFabricWeightNotApplicable:
        transformedQuoteData.isFabricWeightNotApplicable,
      isPlusCostNotApplicable,
    };
  };

  const methods = useForm<CounterQuoteFormValues>({
    defaultValues: {
      type: isAnyRange(counterRanges[0]),
      ...getFormValues(),
    },
  });

  useEffect(() => {
    methods.reset({
      type: isAnyRange(counterRanges[0]),
      ...getFormValues(),
    });
    // Only reset form when switching counters or when quote data changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCounter?.counter_id, selectedCounter?.counter_quote]);

  const onSubmit = async (data: CounterQuoteFormValues) => {
    if (!userId || !selectedCounter) {
      return;
    }
    try {
      const isPlusCostNA = data.isPlusCostNotApplicable ?? false;

      const isBaselineCostEnabled = flags?.baseline_cost?.enabled ?? false;
      const mapRangeData = (range: QuantityItem) => {
        let baselinePlusCostPayload: { baseline_plus_cost?: number | null } =
          {};
        if (isPlusCostNA && isBaselineCostEnabled) {
          baselinePlusCostPayload = { baseline_plus_cost: null };
        } else if (
          range.baseline_plus_cost !== undefined &&
          range.baseline_plus_cost !== ''
        ) {
          baselinePlusCostPayload = {
            baseline_plus_cost: range.baseline_plus_cost,
          };
        }
        return {
          min_range: range.min_range,
          max_range: range.max_range,
          jr_cost: range.jr_cost,
          plus_cost: isPlusCostNA ? null : range.plus_cost,
          ...(range.baseline_jr_cost !== undefined &&
            range.baseline_jr_cost !== '' && {
              baseline_jr_cost: range.baseline_jr_cost,
            }),
          ...baselinePlusCostPayload,
        };
      };

      const fabricData = prepareCounterFormDataForAPI(data);

      const formattedData = {
        counter_quote: {
          ranges:
            data.type === 'range'
              ? (data.quantity as Array<QuantityItem>).map(mapRangeData)
              : [
                  mapRangeData({
                    min_range: -1,
                    max_range: -1,
                    jr_cost: (data.quantity as QuantityItem).jr_cost,
                    plus_cost: (data.quantity as QuantityItem).plus_cost,
                    baseline_jr_cost: (data.quantity as QuantityItem)
                      .baseline_jr_cost,
                    baseline_plus_cost: (data.quantity as QuantityItem)
                      .baseline_plus_cost,
                  }),
                ],
          moq: data.moq,
          mcq: data.mcq,
          lead_time_in_weeks: data.lead_time,
          ro_lead_time_in_weeks: data.reorder_lead_time,
          coo: data.coo,
          shipment_mode: data.shipment_mode,
          fabric_content: fabricData.fabric_content,
          fabric_content_list: fabricData.fabric_content_list,
          fabric_weight:
            fabricData.weight === '' ? '' : fabricData.weight.toString(),
          isFabricWeightNotApplicable: fabricData.isFabricWeightNotApplicable,
          fabric_code_name: fabricData.fabric_code_name,
          fabric_type: fabricData.fabric_type,
          hasPolyurethaneCoating: fabricData.hasPolyurethaneCoating,
          ...(fabricData.contrast && { contrast: fabricData.contrast }),
          ...(fabricData.lining && { lining: fabricData.lining }),
          ...(fabricData.filling && { filling: fabricData.filling }),
          // Legacy-only types (for existing data)
          ...(fabricData.backing && { backing: fabricData.backing }),
          ...(fabricData.coating && { coating: fabricData.coating }),
          ...(data.secondaryFabrics &&
            data.secondaryFabrics.length > 0 && {
              secondaryFabrics: data.secondaryFabrics.map((sf) => ({
                type: sf.type,
                instanceIndex: sf.instanceIndex,
                content: sf.content.map(({ name, percentage }) => ({
                  name,
                  percentage,
                })),
                weight: sf.weight,
                hasPolyurethaneCoating: sf.hasPolyurethaneCoating,
              })),
            }),
          ...(data.isMultiGarment !== undefined && {
            isMultiGarment: data.isMultiGarment,
          }),
          ...(data.isMultiGarment &&
            data.garments && { garments: cleanGarmentsForAPI(data.garments) }),
        },
      };

      await editQuoteMutation.mutateAsync({
        userId,
        counterId: selectedCounter.counter_id,
        data: formattedData,
      });

      toast({ title: 'Quote successfully updated' }, { type: 'success' });
      onClose();
    } catch (error) {
      console.error('Failed to update quote:', error);
      toast({ title: 'Failed to update quote' }, { type: 'error' });
    }
  };

  return (
    <Drawer
      noMargin
      width="600px"
      isOpen={isOpen.opened}
      noBackdrop
      className="h-full"
    >
      <Drawer.Content className="px-0">
        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit(onSubmit)}
            className="h-full flex flex-col"
          >
            <Header closeDrawer={closeDrawer} />
            <div className="border-b border-neutral-200 px-5 flex justify-between items-center">
              <Tabs.Group className="border-b-0">
                <Clickable onClick={() => setSelectedTab('chat')}>
                  <Tabs.Item isActive={selectedTab === 'chat'}>Chat</Tabs.Item>
                </Clickable>
                <Clickable
                  onClick={() => {
                    queryClient.invalidateQueries(QUERY_KEYS.GET_COUNTER);
                    setSelectedTab('quote');
                  }}
                >
                  <Tabs.Item isActive={selectedTab === 'quote'}>
                    Quote & Samples
                  </Tabs.Item>
                </Clickable>
              </Tabs.Group>
              <AvatarGroup max={5}>
                {selectedCounter?.vendor_contacts?.map((contact) => (
                  <Avatar
                    key={contact.id}
                    badgeType="vendor"
                    name={contact.full_name}
                  />
                ))}
                {selectedCounter?.fn_contacts?.map((contact) => (
                  <Avatar
                    key={contact.id}
                    badgeType="fn_internal"
                    name={contact.full_name}
                  />
                ))}
              </AvatarGroup>
            </div>
            {selectedTab === 'quote' ? <QuoteTab /> : <Chat />}
          </form>
        </FormProvider>
      </Drawer.Content>
    </Drawer>
  );
}
